/**
 * Which paint schemes a baked model entry offers, for the model viewer's
 * scheme selector — pure, no DOM.
 *
 * A fighter's `.TBL` ships colour-key markers rather than colours, and the
 * original repaints them at load from tables in `RB.EXE`
 * (docs/formats/shading-and-paint.md §4). The bake resolves that per scheme
 * and writes one entry per scheme: `models/FOKDR1` in the factory colours,
 * `models/FOKDR1@Richthofen` in his all-red ones. Every one of those entries
 * carries the same `paint.options` list, so the selector can offer the whole
 * family from whichever entry the user happened to open.
 */

import type { BakeManifestEntry, ModelMeta, ModelPaintOption } from "@red-opus/baker/schema";

/** The schemes this entry's model family was baked in, default first; `[]` for every model the engine never repaints. */
export function paintSchemes(entry: BakeManifestEntry): readonly ModelPaintOption[] {
  if (entry.category !== "models") return [];
  return (entry.meta as ModelMeta).paint?.options ?? [];
}

/** The scheme label this entry itself is baked in (`"Default"`, `"Richthofen"`), or `undefined` for an unpainted model. */
export function activeScheme(entry: BakeManifestEntry): string | undefined {
  if (entry.category !== "models") return undefined;
  return (entry.meta as ModelMeta).paint?.scheme;
}

/**
 * The baked path to fetch for `scheme`. Falls back to the entry's own path
 * whenever the label is unknown — including for the unpainted models, which
 * have no scheme list at all.
 */
export function schemePath(entry: BakeManifestEntry, scheme: string | undefined): string {
  return paintSchemes(entry).find((option) => option.label === scheme)?.path ?? entry.path;
}
